import { z } from "zod";
import { bookApprovedPackageV1Schema } from "./book-approved-package.js";
// A customer transport is NOT the private package persistence record. Explicit nested
// allowlists prevent validator reports, execution sidecars and object/version keys leaking.
const packageShape = bookApprovedPackageV1Schema.shape;
const safeFile = z.object({
    kind: packageShape.cover.shape.kind,
    checksumSha256: packageShape.cover.shape.checksumSha256,
    byteSize: packageShape.cover.shape.byteSize,
    pageCount: packageShape.cover.shape.pageCount,
    mediaBoxPoints: packageShape.cover.shape.mediaBoxPoints,
});
const safePreflight = z.object({
    preflightId: packageShape.preflight.shape.preflightId,
    preflightDigestSha256: packageShape.preflight.shape.preflightDigestSha256,
    state: packageShape.preflight.shape.state,
    externalPdfValidator: packageShape.preflight.shape.externalPdfValidator,
});
export const customerBookPrintPackageV1Schema = z.object({
    contract: packageShape.contract,
    packageId: packageShape.packageId,
    bookId: packageShape.bookId,
    revisionId: packageShape.revisionId,
    product: packageShape.product,
    editionPolicyId: packageShape.editionPolicyId,
    profileId: packageShape.profileId,
    profileVersion: packageShape.profileVersion,
    semanticSnapshotDigestSha256: packageShape.semanticSnapshotDigestSha256,
    planDigestSha256: packageShape.planDigestSha256,
    state: packageShape.state,
    createdAt: packageShape.createdAt,
    cover: safeFile,
    interior: safeFile,
    preflight: safePreflight,
});
export const customerBookPrintFactsV1Schema = z.object({
    profileId: z.string(),
    profileVersion: z.string(),
    format: z.enum(["BEDTIME", "COMIC", "RECIPE", "MY_STORY", "OUR_STORY"]),
    interiorPageCount: z.number().int().positive(),
    coverPageCount: z.number().int().positive(),
    observedInteriorMediaBoxPoints: z
        .object({ width: z.number().positive(), height: z.number().positive() })
        .nullable(),
    nominalTrimMm: z
        .object({ width: z.number().positive(), height: z.number().positive() })
        .nullable(),
    trimEvidence: z.enum([
        "VERIFIED_EXACT_PRODUCT_TEMPLATE",
        "SESSION_RECORDED_NOT_ARCHIVED",
        "PROFILE_UNAVAILABLE",
    ]),
    binding: z.null(),
    bindingEvidence: z.literal("COMMERCIAL_BINDING_NOT_VERIFIED"),
    includedCopies: z.number().int().nonnegative().nullable(),
    entitlementEvidence: z.enum(["VERIFIED_CLAIMED_ENTITLEMENT", "UNAVAILABLE"]),
    commercialActivation: z.enum([
        "RELEASE_GATED",
        "PROFILE_ACTIVE_NOT_ENTITLEMENT_OR_PAYMENT_AUTHORITY",
    ]),
});
export const customerBookPrintRenderStatusV1Schema = z.object({
    bookId: z.string().min(1).max(240),
    operationId: z.string().min(1).max(240),
    previewId: z.string().min(1).max(240),
    sourceRevision: z.number().int().nonnegative(),
    product: z.enum(["BEDTIME", "COMIC", "RECIPE", "MY_STORY", "OUR_STORY"]),
    state: z.enum(["QUEUED", "RENDERING", "READY_FOR_REVIEW", "FAILED"]),
    packageId: z.string().min(1).max(240).nullable(),
    failure: z
        .object({ code: z.string().regex(/^[A-Z0-9_]{1,120}$/u), retryable: z.boolean() })
        .strict()
        .nullable(),
    updatedAt: z.string().datetime({ offset: true }),
    reviewPath: z.string().max(1_024).nullable(),
});
export const customerBookPrintReviewV1Schema = z.object({
    previewId: z.string().min(1).max(240),
    sourceRevision: z.number().int().nonnegative(),
    expectedCurrentPackageId: z.string().min(1).max(240).nullable(),
    isCurrent: z.boolean(),
    canApprove: z.boolean(),
    delivery: z
        .object({
        entitlementId: z.string().min(1).max(240),
        state: z.enum([
            "PREPARING",
            "AWAITING_PROVIDER_ACTIVATION",
            "READY_FOR_DELIVERY",
            "NEEDS_ATTENTION",
        ]),
    })
        .strict()
        .nullable()
        .optional(),
    package: customerBookPrintPackageV1Schema,
    physicalFacts: customerBookPrintFactsV1Schema.optional(),
});
export const customerBookPrintPdfTicketV1Schema = z.object({
    artifact: z.enum(["COVER", "INTERIOR"]),
    packageId: z.string().min(1).max(240),
    checksumSha256: z.string().regex(/^[a-f0-9]{64}$/u),
    downloadUrl: z
        .string()
        .url()
        .refine((value) => new URL(value).protocol === "https:"),
    expiresIn: z.number().int().min(1).max(300),
});
//# sourceMappingURL=book-print-review.js.map